import { useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import PendingReorderEditCard from "./PendingReorderEditCard";

export function smartReorderResult(item) {
  const result = item.agent === "smart_reorder" && item.action?.type === "smart_reorder.workflow" ? item.action.result : null;
  return result && Array.isArray(result.items) && typeof result.presentation?.headline === "string"
    && Number.isInteger(result.presentation.page_size) && result.presentation.page_size > 0 ? result : null;
}

export function reorderItemLabel(line) {
  const variant = line.variant_label || Object.entries(line.variant_options || {}).map(([name, value]) => `${name}: ${value}`).join(" / ");
  return line.product_name + (variant ? ` · ${variant}` : "");
}

function Fact({ label, value }) {
  return <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 0.7, minWidth: 0 }}>
    <Typography component="dt" variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>{label}</Typography>
    <Typography component="dd" variant="body2" fontWeight={700} sx={{ m: 0, flexShrink: 0 }}><bdi dir="ltr">{value ?? "—"}</bdi></Typography>
  </Box>;
}

const factsGrid = { m: 0, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", columnGap: 1.4, rowGap: 0.5 };

export default function SmartReorderResult({ result, language, canChooseTarget, loading, onEditAction }) {
  const { t, i18n } = useTranslation();
  const lng = language === "ar" || language === "en" ? language : i18n.resolvedLanguage;
  const text = (key, values = {}) => t(`assistant.reorder.${key}`, { lng, ...values });
  const pageSize = result.presentation.page_size;
  const pages = Math.max(1, Math.ceil(result.items.length / pageSize));
  const [page, setPage] = useState(() => Math.min(pages, Math.max(1, result.response_page || 1)));
  const shownPage = Math.min(page, pages);
  const items = result.items.slice((shownPage - 1) * pageSize, shownPage * pageSize);
  const clarification = result.status === "clarification_required";
  const explanation = result.status === "explanation";
  const summary = result.analysis_summary || result.summary;

  return <Box component="section" aria-label={text("title")} lang={lng} dir={lng === "ar" ? "rtl" : "ltr"} sx={{ minWidth: 0, maxWidth: "100%" }}>
    <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1.1 }}>{result.presentation.headline}</Typography>
    {result.pending_edit ? <PendingReorderEditCard pending={result.pending_edit} language={lng} pageSize={pageSize}
      active={canChooseTarget} loading={loading} onAction={onEditAction} /> : clarification ? (
      <>
        <Typography variant="body2" fontWeight={700} sx={{ mb: 0.7 }}>{text("chooseTarget")}</Typography>
      </>
    ) : (
      <>
        {summary && !explanation && <Box sx={{ mb: 1.1 }}>
          <Typography variant="caption" fontWeight={700}>{text(result.analysis_summary ? "storeSummary" : "taskSummary")}</Typography>
          <Box component="dl" sx={factsGrid}>
            <Fact label={text("items")} value={summary.total_recommendations} />
            <Fact label={text("critical")} value={summary.critical_count} />
            <Fact label={text("knownCost")} value={summary.known_cost_subtotal} />
            <Fact label={text("unknownCost")} value={summary.unknown_cost_item_count} />
          </Box>
        </Box>}
        <Stack spacing={1}>
          {items.map((line, index) => <Box component="article" aria-label={reorderItemLabel(line)} key={`${line.product_public_id}:${line.product_variant_public_id || ""}:${index}`}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.15, minWidth: 0, overflowWrap: "anywhere" }}>
            <Typography variant="body2" fontWeight={800} sx={{ mb: 0.7 }}><bdi dir="auto">{line.product_name}</bdi></Typography>
            {line.variant_label && <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.6 }}><bdi dir="auto">{line.variant_label}</bdi></Typography>}
            <Box component="dl" sx={factsGrid}>
              <Fact label={text("available")} value={line.available} />
              <Fact label={text("minimum")} value={line.minimum_stock} />
              <Fact label={text("target")} value={line.target_stock} />
              <Fact label={text("planned")} value={line.planned_quantity} />
              <Fact label={text("incoming")} value={line.incoming_quantity} />
            </Box>
            <Box component="dl" sx={{ ...factsGrid, borderTop: "1px solid", borderColor: "divider", mt: 0.7, pt: 0.7 }}>
              <Fact label={text("recommended")} value={line.recommended_quantity} />
              <Fact label={text("selected")} value={line.selected_quantity} />
            </Box>
          </Box>)}
        </Stack>
      </>
    )}
    {result.presentation.explanation && <Typography variant="body2" sx={{ mt: 1.1, lineHeight: 1.6 }}>{result.presentation.explanation}</Typography>}
    {result.presentation.confirmation_notice && <Typography variant="body2" sx={{ mt: 1.1, lineHeight: 1.6 }}>{result.presentation.confirmation_notice}</Typography>}
    {!result.pending_edit && pages > 1 && <Stack component="nav" aria-label={text("pages")} direction="row" alignItems="center" justifyContent="space-between" gap={0.7} flexWrap="wrap" sx={{ mt: 1.1 }}>
      <Button size="small" disabled={shownPage === 1} onClick={() => setPage(shownPage - 1)}>{text("previous")}</Button>
      <Typography variant="caption">{text("page", { page: shownPage, count: pages })}</Typography>
      <Button size="small" disabled={shownPage === pages} onClick={() => setPage(shownPage + 1)}>{text("next")}</Button>
    </Stack>}
  </Box>;
}
